import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import * as Sentry from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Cookies from 'universal-cookie'
import { NavigateFunction } from 'react-router-dom'
import { TWILIGHT_PREFIX, checkIfTwilightZone } from '../helpers/authentication'
import { adminQueries } from '../api/admin'
import { socket } from '../sockets'
import { CookieKeys } from '../types/cookies'
import { getUser } from '../api/users'
import { retrieveUserSession } from '../api/config'
import { IUserModel } from '../interfaces/models/users.interfaces'

interface AuthProviderProps {
  children: React.ReactNode
}

interface IAuthContext {
  userId: string | undefined
  token: string | null | undefined
  userIsVerified: boolean | undefined
  isBoappaSuperadmin: boolean | undefined
  isTwilightZone: boolean | undefined
  logoutTwilightUser: (navigate: NavigateFunction) => void

  logout: (redirectTo?: string) => void
  setUserId: (id: string) => void
  isLoading: boolean | undefined
}

const AuthContext = createContext<IAuthContext>({
  userId: undefined,
  token: undefined,
  userIsVerified: undefined,
  isBoappaSuperadmin: undefined,
  isTwilightZone: undefined,
  logoutTwilightUser: () => null,
  logout: () => null,
  setUserId: () => null,
  isLoading: undefined,
})
function logout(): void {
  const cookies = new Cookies()
  cookies.remove(CookieKeys.HEADER_PAYLOAD, { path: '/' })
  cookies.remove(CookieKeys.SIGNATURE, { path: '/' })
  cookies.remove(CookieKeys.USER_ID, { path: '/' })

  cookies.remove(`${TWILIGHT_PREFIX}${CookieKeys.HEADER_PAYLOAD}`, {
    path: '/',
  })
  cookies.remove(`${TWILIGHT_PREFIX}${CookieKeys.SIGNATURE}`, { path: '/' })
  cookies.remove(`${TWILIGHT_PREFIX}${CookieKeys.USER_ID}`, { path: '/' })

  socket.unsetSocket()
}
function logoutTwilightUser(): void {
  const cookies = new Cookies()

  cookies.remove(`${TWILIGHT_PREFIX}${CookieKeys.HEADER_PAYLOAD}`, {
    path: '/',
  })
  cookies.remove(`${TWILIGHT_PREFIX}${CookieKeys.SIGNATURE}`, { path: '/' })
  cookies.remove(`${TWILIGHT_PREFIX}${CookieKeys.USER_ID}`, { path: '/' })
}

function getUserIdFromCookie(): string | undefined {
  const cookies = new Cookies()
  const userIdTwilight = cookies.get(
    `${TWILIGHT_PREFIX}${CookieKeys.USER_ID}` // Login as user via superadmin
  )
  const userId = cookies.get(CookieKeys.USER_ID)
  return userIdTwilight || userId || ''
}

const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const initializeState = (): string | undefined => {
    return getUserIdFromCookie()
  }
  const [userId, setUserId] = useState(initializeState)
  const queryClient = useQueryClient()

  const { data: hasAdminPermissions, isLoading: isLoadingAdminPermissions } =
    useQuery(adminQueries.hasPermissions(userId))

  const { mutate: mutateLogoutTwilightUser, isPending: isLogingOutTwilight } =
    useMutation({
      mutationFn: async (navigate: NavigateFunction) => {
        logoutTwilightUser()
        queryClient.clear()
        return navigate
      },
      onSettled: async (navigate) => {
        if (navigate) {
          navigate('admin/twilight-zone')
          window.location.reload()
        }
      },
    })

  const { mutate: mutateLogout, isPending: isLogingOut } = useMutation({
    mutationFn: (redirectTo?: string) => {
      logout()
      return Promise.resolve(redirectTo)
    },

    onSuccess: (redirectTo) => {
      if (redirectTo) {
        window.location.replace(redirectTo)
      } else {
        window.location.reload()
      }
    },
  })

  useEffect(() => {
    Sentry.setUser({ id: userId })
  }, [userId])

  const { data: authContext, isLoading: isVerifyingUser } = useQuery({
    queryKey: [{ scope: 'auth', entity: 'user', userId }],
    queryFn: async () => {
      let _user: IUserModel | undefined
      let isTwilight: boolean | undefined
      let userSession: string | null | undefined
      if (userId) {
        userSession = retrieveUserSession()
        isTwilight = checkIfTwilightZone()
        if (userSession) {
          const response = await getUser(userId)
          if (response.status === 401) {
            logout()
            window.location.reload()
          }
          if (!response.data.data) {
            logout()
            window.location.reload()
          }
          _user = response.data.data
        } else {
          logout()
          window.location.reload()
        }
      }
      return {
        user: _user,
        isTwilight,
        userSession,
      }
    },
    enabled: !!userId,
  })

  const isLoading =
    isLogingOut ||
    isLogingOutTwilight ||
    isVerifyingUser ||
    isLoadingAdminPermissions

  const value = useMemo(
    () => ({
      userId,
      userIsVerified: authContext?.user?.isVerified,
      isBoappaSuperadmin: hasAdminPermissions?.data.data,
      isTwilightZone: authContext?.isTwilight,
      isLoading,
      setUserId: (id: string) => {
        setUserId(id)
      },
      token: authContext?.userSession,
      logoutTwilightUser: (navigate: NavigateFunction) =>
        mutateLogoutTwilightUser(navigate),
      logout: (redirectTo?: string) => {
        mutateLogout(redirectTo)
      },
    }),
    [
      userId,
      authContext?.user?.isVerified,
      authContext?.userSession,
      authContext?.isTwilight,
      hasAdminPermissions?.data.data,
      mutateLogout,
      mutateLogoutTwilightUser,
      isLoading,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.displayName = 'AuthProvider'
export { AuthProvider }

export const useAuth = (): IAuthContext => useContext(AuthContext)
